import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import hadiyaLogo from '../assets/hadiya-logo-web.png';
import icfLogo from '../assets/icf_logo.png';

export default function Login() {
  const [rollNo, setRollNo] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    if (!rollNo.trim()) {
      setError('Roll number is required');
      return false;
    }
    if (!/^[A-Za-z0-9]+$/.test(rollNo.trim())) {
      setError('Roll number must contain only letters and numbers');
      return false;
    }
    if (!password.trim()) {
      setError('Password is required');
      return false;
    }
    if (password.trim().length < 5) {
      setError('Password must be at least 5 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validate()) return;

    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        roll_no: rollNo.trim(),
        password: password.trim()
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/student-react/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid Roll Number or Password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img src={hadiyaLogo} alt="Hadiya Logo" style={styles.logoImage} />
          <h1 style={styles.title}>Welcome Back</h1>
          <p style={styles.subtitle}>Sign in to access your dashbord</p>
        </div>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Roll Number</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>ID</span>
              <input
                type="text"
                value={rollNo}
                onChange={e => setRollNo(e.target.value)}
                style={styles.input}
                placeholder="Enter your roll number"
                disabled={loading}
                autoComplete="off"
              />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>•••</span>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.input}
                placeholder="First 5 letters of your name"
                disabled={loading}
              />
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? <span style={styles.loadingSpinner}>...</span> : 'Sign In'}
          </button>
        </form>
        <div style={styles.footer}>
          <img src={icfLogo} alt="ICF Logo" style={styles.footerLogo} />
          <p style={styles.footerText}>ICF INTERNATIONAL</p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 50%, #1a202c 100%)',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(66, 153, 225, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(237, 100, 166, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    padding: '48px 40px',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    width: '100%',
    maxWidth: '440px',
    animation: 'fadeInUp 0.6s ease-out',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  logoImage: {
    height: '80px',
    objectFit: 'contain',
    marginBottom: '16px',
  },
  title: {
    margin: '0 0 8px',
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    margin: 0,
    fontSize: '15px',
    color: '#718096',
    fontWeight: '400',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#4a5568',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    fontSize: '18px',
    zIndex: 1,
  },
  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#f7fafc',
    color: '#2d3748',
  },
  button: {
    padding: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    letterSpacing: '0.5px',
  },
  loadingSpinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  error: {
    background: '#fed7d7',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    borderLeft: '4px solid #c53030',
    animation: 'shake 0.5s ease-in-out',
  },
  footer: {
    marginTop: '32px',
    textAlign: 'center',
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  footerLogo: {
    height: '40px',
    objectFit: 'contain',
  },
  footerText: {
    margin: 0,
    fontSize: '13px',
    color: '#a0aec0',
    fontWeight: '500',
  },
};
